import { Action, Dispatch } from 'redux';
import { RootStateOrAny } from 'react-redux';
import { ThunkAction } from 'redux-thunk';
import { CurrentUserReducer, RootReducer } from '../../models/Redux';
import {
    allowTurn,
    finishEvent,
    getCurrentEvent,
    getStreamingState,
    joinStreaming,
    sendMessage,
    startStreaming,
    toggleAdminUser,
    toggleBlockUser,
    toggleChat,
    toggleOpenMic,
    toggleRaiseHand,
    toggleRaiseHandViewer,
} from '../../services/EventService';
import { StreamingState, ViewerState } from '../../models/StreamingState';
import { EVENT_UUID } from '../../environment/environment';

export const SET_STREAMING_FLAGS = 'SET_STREAMING_FLAGS';
export const SET_EVENT_UUID = 'SET_EVENT_UUID';
export const SET_CURRENT_EVENT = 'SET_CURRENT_EVENT';
export const SET_STREAMING_STATE = 'SET_STREAMING_STATE';
export const SET_LOADING_STREAMING = 'SET_LOADING_STREAMING';
export const SET_LOCAL_STREAMING_STATE = 'SET_LOCAL_STREAMING_STATE';
export const PUSH_STREAMING_CHAT_MESSAGE = 'PUSH_STREAMING_CHAT_MESSAGE';
export const FILTER_USER_MESSAGES_STREAMING_CHAT_MESSAGE = 'FILTER_USER_MESSAGES_STREAMING_CHAT_MESSAGE';
export const ERROR_FETCHING_STREAMING = 'ERROR_FETCHING_STREAMING';

const checkIfViewerIsAskingForTurn = (
    dispatch: Dispatch,
    getState: () => RootStateOrAny,
    newStreamingState: StreamingState,
) => {
    try {
        const isWaitingForTurn = !!newStreamingState.current_state.raised_hands?.find(
            (viewerWaiting: ViewerState) =>
                viewerWaiting.username === getState().currentUser.user.user_username,
        );

        dispatch({
            type: SET_LOCAL_STREAMING_STATE,
            payload: {
                ...getState().streaming.localStreamingState,
                raiseHand: isWaitingForTurn,
            },
        });
    } catch (e) {
        console.error(e);
    }
};

export const thunkGetCurrentEvent = (
    uuid?: string,
    callback?: () => void,
): ThunkAction<void, CurrentUserReducer, unknown, Action<string>> => dispatch => {
    dispatch({ type: SET_LOADING_STREAMING, payload: true });
    getCurrentEvent(uuid || EVENT_UUID || '')
        .then(payload => {
            dispatch({
                type: SET_CURRENT_EVENT,
                payload,
            });
            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkStartEvent = (
    callback?: () => void,
    errorCallback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    dispatch({ type: SET_LOADING_STREAMING, payload: true });
    startStreaming(getState().streaming.eventUuid || '')
        .then(payload => {
            dispatch({
                type: SET_STREAMING_STATE,
                payload,
            });
            dispatch({
                type: SET_STREAMING_FLAGS,
                payload: { ...getState().streaming.streamingFlags, started: true },
            });
            callback?.();
        })
        .catch(() => {
            try {
                errorCallback?.();
            } catch (e) {}
            dispatch({ type: ERROR_FETCHING_STREAMING });
        });
};

export const thunkJoinEvent = (
    callback?: () => void,
    errorCallback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    dispatch({ type: SET_LOADING_STREAMING, payload: true });
    joinStreaming(getState().streaming.eventUuid || '')
        .then(payload => {
            dispatch({
                type: SET_STREAMING_STATE,
                payload,
            });
            dispatch({
                type: SET_STREAMING_FLAGS,
                payload: { ...getState().streaming.streamingFlags, started: true },
            });

            checkIfViewerIsAskingForTurn(dispatch, getState, payload);
            callback?.();
        })
        .catch(() => {
            try {
                errorCallback?.();
            } catch (e) {}
            dispatch({ type: ERROR_FETCHING_STREAMING });
        });
};

export const thunkGetStreamingStateEvent = (
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    dispatch({ type: SET_LOADING_STREAMING });
    getStreamingState(getState().streaming.eventUuid || '')
        .then(payload => {
            dispatch({
                type: SET_STREAMING_STATE,
                payload,
            });

            try {
                const {
                    current_state: { speaking_viewer: speakingViewer },
                } = payload;
                const hasTurn = speakingViewer?.username === getState().currentUser.user.user_username;
                dispatch({
                    type: SET_STREAMING_FLAGS,
                    payload: { ...getState().streaming.streamingFlags, hasTurn },
                });
            } catch (e) {}

            checkIfViewerIsAskingForTurn(dispatch, getState, payload);

            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkToggleOpenMicEvent = (
    enabled: boolean,
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    toggleOpenMic(enabled, getState().streaming.eventUuid || '')
        .then(() => {
            const newStreamingState = { ...getState().streaming.streamingState };

            try {
                if (newStreamingState.current_state) newStreamingState.current_state.open_mic = enabled;
            } catch (e) {}

            dispatch({
                type: SET_STREAMING_STATE,
                payload: newStreamingState,
            });

            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkToggleRaiseHandEvent = (
    enabled: boolean,
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    toggleRaiseHand(enabled, getState().streaming.eventUuid || '')
        .then(() => {
            const newStreamingState = { ...getState().streaming.streamingState };

            try {
                if (newStreamingState.current_state)
                    newStreamingState.current_state.raised_hand_enabled = enabled;
            } catch (e) {}

            dispatch({
                type: SET_STREAMING_STATE,
                payload: newStreamingState,
            });

            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkToggleChat = (
    enabled: boolean,
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    toggleChat(enabled, getState().streaming.eventUuid || '')
        .then(() => {
            const newStreamingState = { ...getState().streaming.streamingState };

            try {
                if (newStreamingState.current_state) newStreamingState.current_state.chat_enabled = enabled;
            } catch (e) {}

            dispatch({
                type: SET_STREAMING_STATE,
                payload: newStreamingState,
            });

            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkToggleRaiseHand = (
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    toggleRaiseHandViewer(getState().streaming.eventUuid || '')
        .then(payload => {
            const newStreamingState = { ...getState().streaming.streamingState };

            try {
                const {
                    current_state: { raised_hands: raiseHands },
                } = payload;
                if (newStreamingState.current_state)
                    newStreamingState.current_state.raised_hands = raiseHands;
            } catch (e) {}

            checkIfViewerIsAskingForTurn(dispatch, getState, newStreamingState as StreamingState);

            dispatch({
                type: SET_STREAMING_STATE,
                payload: newStreamingState,
            });

            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkAllowViewerTurn = (
    username: string,
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    allowTurn(username, getState().streaming.eventUuid || '')
        .then(payload => {
            const { speaking_viewer: speakingViewer } = payload;
            const newStreamingState = { ...getState().streaming.streamingState };

            try {
                if (newStreamingState.current_state)
                    newStreamingState.current_state.speaking_viewer = speakingViewer;
            } catch (e) {}

            dispatch({
                type: SET_STREAMING_STATE,
                payload: newStreamingState,
            });

            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkSendMessage = (
    message: string,
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    sendMessage(message, getState().streaming.eventUuid || '')
        .then(() => {
            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkToggleBlockUser = (
    username: string,
    enabled?: boolean,
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    toggleBlockUser(username, getState().streaming.eventUuid || '')
        .then(() => {
            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkToggleAdminUser = (
    username: string,
    enabled?: boolean,
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    toggleAdminUser(username, getState().streaming.eventUuid || '')
        .then(() => {
            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};

export const thunkFinishEvent = (
    callback?: () => void,
): ThunkAction<void, RootReducer, unknown, Action<string>> => (dispatch, getState) => {
    finishEvent(getState().streaming.eventUuid || '')
        .then(() => {
            const newStreamingState = { ...getState().streaming.streamingState };

            try {
                if (newStreamingState.current_state)
                    newStreamingState.current_state.raised_hand_enabled = undefined;
            } catch (e) {}

            dispatch({
                type: SET_STREAMING_STATE,
                payload: newStreamingState,
            });

            callback?.();
        })
        .catch(() => dispatch({ type: ERROR_FETCHING_STREAMING }));
};
